import { ArcLayer, ScatterplotLayer } from "@deck.gl/layers";

// 波及アークの色（source=cyan / target=orange）と太さスケール。
const ARC_SOURCE = [46, 184, 212, 230];
const ARC_TARGET = [232, 133, 58, 210];
function arcWidth(capacity) {
  return Math.min(Math.max(Math.sqrt(Math.max(capacity ?? 0, 0)) / 110, 1.2), 7);
}

// DESIGN.md §2 のシグナル色を RGB 配列で持つ（deck.gl は [r,g,b,a]）。
const SIGNAL_RGB = {
  ok: [52, 210, 123],
  warn: [229, 168, 22],
  danger: [229, 77, 77],
};

function signalColor(signal, alpha = 255) {
  const rgb = SIGNAL_RGB[signal] ?? SIGNAL_RGB.ok;
  return [rgb[0], rgb[1], rgb[2], alpha];
}

// sqrt スケールで取扱量を半径(px)へ。小さすぎ/大きすぎをクランプ（DESIGN.md §5）。
function radiusFor(value, k, min, max) {
  const r = Math.sqrt(Math.max(value ?? 0, 0)) * k;
  return Math.min(Math.max(r, min), max);
}

/**
 * 監視データから deck.gl のデータレイヤー一覧を構築する。
 * ベースマップは MapLibre（MapTiler ベクタースタイル）が下に描くため、ここには含めない。
 * @param {object|null} data portwatch.json（未ロード時は空）
 * @param {object} handlers { onHover, onClick, selectedId, network }
 */
export function renderLayers(data, handlers = {}) {
  const { onHover, onClick, selectedId, network } = handlers;

  const layers = [];

  // 波及ネットワーク（選択港 from → 影響先港 to）。マーカーより下に敷く。
  if (network?.from && network.arcs?.length) {
    layers.push(
      new ArcLayer({
        id: "spillover-arcs",
        data: network.arcs,
        greatCircle: true,
        getSourcePosition: () => network.from,
        getTargetPosition: (d) => d.to,
        getSourceColor: ARC_SOURCE,
        getTargetColor: ARC_TARGET,
        getWidth: (d) => arcWidth(d.capacityAtRisk),
        widthUnits: "pixels",
        pickable: true,
      }),
      new ScatterplotLayer({
        id: "spillover-targets",
        data: network.arcs,
        getPosition: (d) => d.to,
        getRadius: 4,
        radiusUnits: "pixels",
        getFillColor: ARC_TARGET,
        stroked: false,
        pickable: false,
      }),
    );
  }

  if (!data) return layers;

  const ports = (data.ports ?? []).filter((p) => p.lat != null && p.lon != null);
  const chokepoints = (data.chokepoints ?? []).filter(
    (c) => c.lat != null && c.lon != null,
  );

  const updateTriggers = { selected: selectedId };

  // danger の地点に広がるハロー（視線誘導。アニメーションせず静的な波紋）。
  const dangerHalo = new ScatterplotLayer({
    id: "danger-halo",
    data: [...chokepoints, ...ports].filter((d) => d.signal === "danger"),
    getPosition: (d) => [d.lon, d.lat],
    getRadius: 16,
    radiusUnits: "pixels",
    getFillColor: signalColor("danger", 40),
    stroked: false,
    pickable: false,
  });

  // 港: ScatterplotLayer。signal 色で塗り、取扱量(portcalls)で半径。
  const portLayer = new ScatterplotLayer({
    id: "ports",
    data: ports,
    getPosition: (d) => [d.lon, d.lat],
    getFillColor: (d) => signalColor(d.signal, 217),
    getLineColor: [11, 15, 25, 230],
    getRadius: (d) => radiusFor(d.latest?.portcalls, 1.2, 4, 22),
    radiusUnits: "pixels",
    stroked: true,
    lineWidthMinPixels: 1,
    pickable: true,
    onHover: onHover,
    onClick: (info) => onClick?.(info.object),
    updateTriggers,
  });

  // チョークポイント: 中空リングで差別化（港より重要）。
  const chokeLayer = new ScatterplotLayer({
    id: "chokepoints",
    data: chokepoints,
    getPosition: (d) => [d.lon, d.lat],
    getFillColor: (d) => signalColor(d.signal, 60),
    getLineColor: (d) => signalColor(d.signal, 255),
    getRadius: (d) => radiusFor(d.latest?.n_total, 2.4, 8, 20),
    radiusUnits: "pixels",
    stroked: true,
    filled: true,
    lineWidthMinPixels: 2.5,
    pickable: true,
    onHover: onHover,
    onClick: (info) => onClick?.(info.object),
    updateTriggers,
  });

  layers.push(dangerHalo, chokeLayer, portLayer);

  // 選択地点に白リングを重ねる。
  const selected = [...chokepoints, ...ports].find(
    (d) => d.portid === selectedId,
  );
  if (selected) {
    layers.push(
      new ScatterplotLayer({
        id: "selected-ring",
        data: [selected],
        getPosition: (d) => [d.lon, d.lat],
        getRadius: 26,
        radiusUnits: "pixels",
        stroked: true,
        filled: false,
        lineWidthMinPixels: 2,
        getLineColor: [225, 231, 239, 235],
        pickable: false,
      }),
    );
  }

  return layers;
}
